import { useState, useEffect, useRef } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';

export default function CatalogTracker() {
  const checkAuth = useAuthGuard();
  const [trackers, setTrackers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [checkingAll, setCheckingAll] = useState(false);
  const [catalogUrl, setCatalogUrl] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [error, setError] = useState('');
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [checkingMsg, setCheckingMsg] = useState('');
  const pollingRef = useRef<any>(null);

  const fetchTrackers = async () => {
    try {
      const res = await client.get('/catalog');
      setTrackers(res.data.trackers || []);
    } catch {}
  };

  useEffect(() => { fetchTrackers(); }, []);

  // 컴포넌트 언마운트 시 폴링 정리
  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  const handleRegister = async () => {
    if (!checkAuth()) return;
    if (!catalogUrl || !storeUrl) return setError('두 URL을 모두 입력해주세요');
    setRegisterLoading(true); setError('');
    try {
      await client.post('/catalog', { catalog_url: catalogUrl, my_store_url: storeUrl });
      setCatalogUrl(''); setStoreUrl('');
      await fetchTrackers();
    } catch (e: any) {
      setError(e.response?.data?.detail || '등록 실패');
    } finally { setRegisterLoading(false); }
  };

  const handleCheck = async (id: number) => {
    if (checkingId !== null) return;
    setCheckingId(id);
    setCheckingMsg('순위 조회 요청 중...');

    try {
      // Job 생성
      const jobRes = await client.post(`/catalog/${id}/job`);
      const jobId = jobRes.data.job_id;

      setCheckingMsg('워커 PC에서 카탈로그 분석 중...');

      // 폴링 시작 (2초마다)
      let elapsed = 0;
      pollingRef.current = setInterval(async () => {
        elapsed += 2;
        try {
          const statusRes = await client.get(`/catalog/job/${jobId}/status`);
          const { status, result, error: errMsg } = statusRes.data;

          if (status === 'done') {
            clearInterval(pollingRef.current);
            setCheckingId(null);
            setCheckingMsg('');
            await fetchTrackers();
          } else if (status === 'failed') {
            clearInterval(pollingRef.current);
            setCheckingId(null);
            setCheckingMsg('');
            alert(`순위 조회 실패: ${errMsg || '알 수 없는 오류'}`);
          } else if (elapsed >= 60) {
            // 60초 타임아웃
            clearInterval(pollingRef.current);
            setCheckingId(null);
            setCheckingMsg('');
            alert('워커 PC가 응답하지 않습니다. PC에서 worker.py가 실행 중인지 확인해주세요.');
          } else {
            setCheckingMsg(`워커 PC에서 처리 중... (${elapsed}초)`);
          }
        } catch {
          clearInterval(pollingRef.current);
          setCheckingId(null);
          setCheckingMsg('');
        }
      }, 2000);

    } catch (e: any) {
      setCheckingId(null);
      setCheckingMsg('');
      alert(e.response?.data?.detail || '작업 생성 실패');
    }
  };

  const handleCheckAll = async () => {
    setCheckingAll(true);
    try {
      await client.post('/catalog/check-all');
      await fetchTrackers();
    } catch {} finally { setCheckingAll(false); }
  };

  const handleToggleAuto = async (id: number) => {
    try {
      await client.post(`/catalog/${id}/auto-track`);
      await fetchTrackers();
    } catch {}
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await client.delete(`/catalog/${id}`);
      await fetchTrackers();
    } catch {}
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 13, boxSizing: 'border-box' as const },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '6px 14px', color: '#8892A4', fontSize: 12, cursor: 'pointer' },
    btnDanger: { background: 'none', border: '0.5px solid #F5656544', borderRadius: 6, padding: '6px 14px', color: '#F56565', fontSize: 12, cursor: 'pointer' },
  };

  const rankColor = (rank: number | null) => {
    if (!rank) return '#4A5568';
    if (rank === 1) return '#F6AD55';
    if (rank <= 3) return '#00D4AA';
    if (rank <= 10) return '#378ADD';
    return '#8892A4';
  };

  return (
    <Layout>
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>원부(가격비교) 순위 추적</h2>
          <p style={{ color: '#8892A4', fontSize: 12, margin: 0 }}>네이버 쇼핑 카탈로그에서 내 상품의 가격 순위를 추적합니다</p>
        </div>

        {/* 등록 폼 */}
        <div style={s.card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ color: '#8892A4', fontSize: 11, display: 'block', marginBottom: 6 }}>원부(카탈로그) URL</label>
              <input style={s.input} value={catalogUrl} onChange={e => setCatalogUrl(e.target.value)}
                placeholder="https://search.shopping.naver.com/catalog/..." />
            </div>
            <div>
              <label style={{ color: '#8892A4', fontSize: 11, display: 'block', marginBottom: 6 }}>내 스토어 상품 URL</label>
              <input style={s.input} value={storeUrl} onChange={e => setStoreUrl(e.target.value)}
                placeholder="https://smartstore.naver.com/..." />
            </div>
          </div>
          {error && <p style={{ color: '#F56565', fontSize: 12, marginBottom: 8 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={s.btn} onClick={handleRegister} disabled={registerLoading}>
              {registerLoading ? '등록 중... (상품 정보 수집 중)' : '+ 원부 등록'}
            </button>
            {trackers.length > 0 && (
              <button style={{ ...s.btnGhost, color: '#00D4AA', borderColor: '#00D4AA44' }}
                onClick={handleCheckAll} disabled={checkingAll}>
                {checkingAll ? '조회 중...' : `⟳ 전체 조회 (${trackers.length}개)`}
              </button>
            )}
          </div>
        </div>

        {/* 트래커 목록 */}
        {trackers.map(t => (
          <div key={t.id} style={s.card}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {t.thumbnail && (
                <img src={t.thumbnail} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t.name || `카탈로그 ${t.catalog_id}`}</div>
                    <div style={{ color: '#4A5568', fontSize: 11 }}>스토어: {t.my_store_name}</div>
                  </div>
                  {/* 순위 배지 */}
                  <div style={{ textAlign: 'center' as const, background: '#0F1117', borderRadius: 8, padding: '8px 16px', minWidth: 70 }}>
                    <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 2 }}>현재 순위</div>
                    <div style={{ color: rankColor(t.last_rank), fontSize: 24, fontWeight: 900 }}>
                      {t.last_rank ? `${t.last_rank}위` : '-'}
                    </div>
                    {t.last_checked && (
                      <div style={{ color: '#4A5568', fontSize: 10 }}>
                        {new Date(t.last_checked).toLocaleDateString('ko-KR')}
                      </div>
                    )}
                  </div>
                </div>

                {/* 가격 정보 */}
                {t.last_top_price && (
                  <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                    <div style={{ background: '#0F1117', borderRadius: 6, padding: '6px 12px' }}>
                      <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 2 }}>1위 가격</div>
                      <div style={{ color: '#F6AD55', fontSize: 14, fontWeight: 700 }}>{t.last_top_price?.toLocaleString()}원</div>
                    </div>
                    {t.last_my_price && (
                      <div style={{ background: '#0F1117', borderRadius: 6, padding: '6px 12px' }}>
                        <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 2 }}>내 가격</div>
                        <div style={{ color: '#00D4AA', fontSize: 14, fontWeight: 700 }}>{t.last_my_price?.toLocaleString()}원</div>
                      </div>
                    )}
                    {t.last_top_price && t.last_my_price && t.last_my_price > t.last_top_price && (
                      <div style={{ background: '#2A1F0D', borderRadius: 6, padding: '6px 12px', border: '0.5px solid #F6AD5544' }}>
                        <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 2 }}>가격 차이</div>
                        <div style={{ color: '#F56565', fontSize: 14, fontWeight: 700 }}>+{(t.last_my_price - t.last_top_price).toLocaleString()}원</div>
                      </div>
                    )}
                  </div>
                )}

                {/* 조회 중 메시지 */}
                {checkingId === t.id && (
                  <div style={{ background: '#0F2A22', border: '0.5px solid #00D4AA44', borderRadius: 6, padding: '8px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                    <span style={{ color: '#00D4AA', fontSize: 12 }}>{checkingMsg}</span>
                  </div>
                )}

                {/* 버튼 */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                  <a href={t.catalog_url} target="_blank" rel="noreferrer" style={{ ...s.btnGhost, textDecoration: 'none', display: 'inline-block' }}>원부 보기</a>
                  <button style={s.btnGhost} onClick={() => handleCheck(t.id)} disabled={checkingId !== null}>
                    {checkingId === t.id ? '조회 중...' : '⟳ 순위 조회'}
                  </button>
                  <button
                    onClick={() => handleToggleAuto(t.id)}
                    style={{
                      ...s.btnGhost,
                      color: t.auto_track ? '#00D4AA' : '#8892A4',
                      borderColor: t.auto_track ? '#00D4AA55' : '#2A3045',
                      background: t.auto_track ? '#0F2A22' : 'none',
                    }}>
                    {t.auto_track ? '● 자동추적 ON' : '○ 자동추적 OFF'}
                  </button>
                  <button style={s.btnDanger} onClick={() => handleDelete(t.id)}>삭제</button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {trackers.length === 0 && !loading && (
          <div style={{ ...s.card, textAlign: 'center' as const, color: '#4A5568', padding: 40 }}>
            등록된 원부가 없습니다. 위에서 카탈로그 URL과 스토어 URL을 입력해 등록하세요.
          </div>
        )}
      </div>
    </Layout>
  );
}
